/* eslint-disable no-shadow */
import { WithModal } from 'hocs/withModal';
import React, { useImperativeHandle } from 'react';
import { Formik, FieldArray } from 'formik';
import { Form, Button, List, Row, Col, Space, notification } from 'antd-v5';
import {
  DatePickerField,
  InfiniteSelectField,
  RadioGroupField,
  TextField,
} from 'components/Molecules/FormFields/index';
import { PlusCircleFilled } from '@ant-design/icons';
import { removeKey } from 'helpers/index';
import get from 'lodash/get';
import T from 'i18n-react';
import * as Yup from 'yup';
import { useCreate } from 'context/ResourceProvider/hooks/useCreate';
import FormObserver from 'components/New/FormStepper/FormObserver';
import classes from './Other.module.scss';

const OtherData = React.forwardRef(({ initialValues, onSubmit }, ref) => {
  const formRef = React.useRef();
  const { mutate: createIssuePartyOption, loading: creatingIssueParty } = useCreate({
    resource: 'compliance-custom-options',
  });
  const { mutate: createCustomOtherType, loading: creatingCustomOtherType } = useCreate({
    resource: 'compliance-custom-options',
  });
  const handleCreateIssueParty = async (newOption, { onChange, onSelect }) => {
    const response = await createIssuePartyOption({
      type: 'issueParty',
      name: newOption.label,
    });
    const createdOption = get(response, 'data.data');
    if (createdOption) {
      onChange(createdOption.id, {
        label: createdOption.name,
        value: createdOption.id,
        data: createdOption,
      });
      onSelect();
    } else {
      notification.error({
        message: 'Create new type failed',
      });
    }
  };
  const handleCreateCustomOtherType = async (newOption, { onChange, onSelect }) => {
    const response = await createCustomOtherType({
      type: 'complianceOtherDataType',
      name: newOption.label,
    });
    const createdOption = get(response, 'data.data');
    if (createdOption) {
      onChange(createdOption.id, {
        label: createdOption.name,
        value: createdOption.id,
        data: createdOption,
      });
      onSelect();
    } else {
      notification.error({
        message: 'Create new type failed',
      });
    }
  };
  const handleAction = (listValue, arrayHelpers, meta = {}) => () => {
    const { action, index } = meta;
    switch (action) {
      case 'add': {
        const newTestData = {
          customComplianceOtherDataTypeId: '',
          customIssuePartyId: '',
          result: '',
        };
        arrayHelpers.push(newTestData);
        break;
      }
      case 'remove': {
        arrayHelpers.remove(index);
        break;
      }
      case 'duplicate': {
        const duplicateTestData = listValue[index];
        arrayHelpers.push(removeKey(duplicateTestData, 'id'));
        break;
      }
      default: {
        break;
      }
    }
  };
  useImperativeHandle(ref, () => ({
    submit: () => {
      return formRef.current.submitForm();
    },
  }));

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={Yup.object({
        otherData: Yup.array().of(
          Yup.object({
            customComplianceOtherDataTypeId: Yup.string()
              .nullable()
              .required(' '),
            result: Yup.string()
              .nullable()
              .required(' '),
            customIssuePartyId: Yup.string()
              .nullable()
              .required(' '),
          })
        ),
      })}
      onSubmit={onSubmit}>
      {(formikProps) => {
        formRef.current = formikProps;
        const otherData = formikProps.values.otherData || [];
        return (
          <Form
            layout="vertical"
            requiredMark={(label, { required }) => (
              <>
                {label} {required ? <sup>*</sup> : <sub>*</sub>}
              </>
            )}>
            <FormObserver
              onValidateChange={(errors) => {
                if (formikProps.submitCount > 0) {
                  formikProps.setTouched(errors);
                }
              }}
            />
            <FieldArray name="otherData">
              {(arrayHelpers) => {
                return (
                  <List
                    dataSource={otherData}
                    className={classes.other}
                    itemLayout="vertical"
                    split={false}
                    footer={
                      <Button
                        type="link"
                        icon={<PlusCircleFilled />}
                        onClick={handleAction(otherData, arrayHelpers, {
                          action: 'add',
                        })}>
                        Other
                      </Button>
                    }
                    renderItem={(_, index) => {
                      return (
                        <List.Item className={classes.item}>
                          <List.Item.Meta
                            title={
                              <div className={classes.header}>
                                <span className={classes.title}># {index + 1}</span>
                                <Space>
                                  <Button
                                    type="primary"
                                    ghost
                                    onClick={handleAction(otherData, arrayHelpers, {
                                      action: 'duplicate',
                                      index,
                                    })}>
                                    Duplicate
                                  </Button>
                                  <Button
                                    type="primary"
                                    ghost
                                    danger
                                    disabled={otherData.length <= 1}
                                    onClick={handleAction(otherData, arrayHelpers, {
                                      action: 'remove',
                                      index,
                                    })}>
                                    Remove
                                  </Button>
                                </Space>
                              </div>
                            }
                          />
                          <RadioGroupField
                            layoutClasses={classes.testResult}
                            label={<h5 className={classes.subtitle}>Result</h5>}
                            required
                            name={`otherData.[${index}].result`}
                            options={[
                              { value: 'pass', label: 'Passed' },
                              {
                                value: 'fail',
                                label: 'Failed',
                              },
                              { value: 'notAvailable', label: 'N/A' },
                            ]}
                          />
                          <Row gutter={[16, 16]}>
                            <Col span={12}>
                              <InfiniteSelectField
                                resource="compliance-custom-options"
                                filters={{
                                  type: 'complianceOtherDataType',
                                }}
                                required
                                name={`otherData.[${index}].customComplianceOtherDataTypeId`}
                                label="Type"
                                placeholder={T.translate(`placeholder.fillOrSelect`)}
                                showArrow
                                creatable
                                onChange={({ field }, _, option) => {
                                  field.onChange({
                                    target: {
                                      name: `otherData.[${index}].customComplianceOtherDataTypeId`,
                                      value: option,
                                    },
                                  });
                                }}
                                onCreate={handleCreateCustomOtherType}
                                loading={creatingCustomOtherType}
                              />
                            </Col>
                            <Col span={12}>
                              <InfiniteSelectField
                                resource="compliance-custom-options"
                                filters={{
                                  type: 'issueParty',
                                }}
                                required
                                name={`otherData.[${index}].customIssuePartyId`}
                                label="Issue Party"
                                placeholder={T.translate(`placeholder.fillOrSelect`)}
                                creatable
                                showArrow
                                onCreate={handleCreateIssueParty}
                                loading={creatingIssueParty}
                                onChange={({ field }, _, option) => {
                                  field.onChange({
                                    target: {
                                      name: `otherData.[${index}].customIssuePartyId`,
                                      value: option,
                                    },
                                  });
                                }}
                              />
                            </Col>
                          </Row>
                          <Row gutter={[16, 16]}>
                            <Col span={12}>
                              <TextField
                                placeholder={T.translate('placeholder.fillHere')}
                                name={`otherData.[${index}].name`}
                                label="Name"
                              />
                            </Col>
                            <Col span={12}>
                              <TextField
                                placeholder={T.translate('placeholder.fillHere')}
                                name={`otherData.[${index}].description`}
                                label="Description"
                              />
                            </Col>
                          </Row>

                          <Row gutter={[16, 16]}>
                            <Col span={12}>
                              <DatePickerField
                                name={`otherData.[${index}].expiryDate`}
                                label="Expiry Date"
                              />
                            </Col>
                            <Col span={12}>
                              <DatePickerField
                                name={`otherData.[${index}].testDate`}
                                label="Issue Date"
                              />
                            </Col>
                          </Row>
                        </List.Item>
                      );
                    }}
                  />
                );
              }}
            </FieldArray>
          </Form>
        );
      }}
    </Formik>
  );
});
export default WithModal(OtherData);
